import React, {
    DragEvent,
    HTMLAttributes,
    forwardRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { attachmentCompose } from 'flow/actions/chat-actions';
import GenericListSkeleton from 'flow/components/loaders/GenericListSkeleton';
import { MessageThreadCheckProvider } from 'flow/contexts/MessageThreadCheckProvider';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useRovingMessageList from 'flow/hooks/useRovingMessageList';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { MAX_ALLOWED_ATTACHMENT_SIZE } from 'flow/utils/message-utils';
import ExternalChatMessage from 'flow/pages/messages/ExternalChatMessage';
import ChatMessageRow from './ChatMessageRow';
import ChatPostRow from './ChatPostRow';
import DateDivider from './DateDivider';
import { ListItem } from './types';
import UnreadDivider from './UnreadDivider';
import { useMessageListData } from './useMessageListData';

const SCROLL_READY_DELAY = 300;
const SCROLL_TOP_THRESHOLD = 200;
const AT_BOTTOM_THRESHOLD = 50;

const Scroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ style, className, ...props }, ref) => {
        // Whether the initial scroll position has settled. Until it has,
        // scrollbar-deferred keeps the thumb invisible so it does not flicker
        // while the list is being measured and moved; after it, CSS shows the
        // thumb. Both classes are needed: on its own, .scrollbar means the
        // ordinary always-visible thumb every other list has.
        const [settled, setSettled] = useState(false);

        useEffect(() => {
            const timer = setTimeout(
                () => setSettled(true),
                SCROLL_READY_DELAY,
            );
            return () => clearTimeout(timer);
        }, []);

        return (
            <div
                ref={ref}
                style={style}
                {...props}
                className={twMerge(
                    className,
                    'scrollbar scrollbar-deferred',
                    settled && 'scrollbar-ready',
                )}
            />
        );
    },
);
Scroller.displayName = 'Scroller';

/**
 * Splits the flat item list into one box per date, each starting with that
 * date's divider. Anything before the first divider - there should be nothing,
 * but the list is not the one deciding that - keeps its place in a leading box.
 */
export const groupItemsByDate = (items: ListItem[]) => {
    const groups: { key: string; items: ListItem[] }[] = [];
    const timesDateSeen = new Map<string, number>();

    items.forEach((item) => {
        if (item.type === 'date-divider' || groups.length === 0) {
            let key = 'before-first-date';

            if (item.type === 'date-divider') {
                /* A date can legitimately appear twice: the divider is emitted
                   whenever the date changes from the previous item, and a
                   failed send sorts after everything else regardless of when
                   it was written - so a failed message from an earlier day
                   reopens that day at the end. Keyed on the date alone, two
                   sibling boxes would share a key and React could reuse a
                   whole day's rows for the wrong day.

                   Counted per date rather than by position in the list,
                   because paging in history adds earlier days at the front:
                   a positional key would change for every box below them, and
                   React would remount the lot - restarting a playing video and
                   re-requesting every image. Older messages for a day already
                   loaded stay contiguous with it, so they add no occurrence
                   and leave these keys alone. */
                const occurrence = timesDateSeen.get(item.date) ?? 0;
                timesDateSeen.set(item.date, occurrence + 1);
                key = `${item.date}-${occurrence}`;
            }

            groups.push({ key, items: [] });
        }
        groups[groups.length - 1].items.push(item);
    });

    return groups;
};

/**
 * Wrapper that observes its own content height. When the content grows
 * (e.g. a reaction animates open) while the scroller is at the bottom,
 * it adjusts scrollTop to keep the bottom pinned.
 */
const ContentWrapper = ({
    scrollerRef,
    atBottomRef,
    children,
}: {
    scrollerRef: React.RefObject<HTMLDivElement>;
    atBottomRef: React.MutableRefObject<boolean>;
    children: React.ReactNode;
}) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const prevHeightRef = useRef(0);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            const newHeight = el.offsetHeight;
            const delta = newHeight - prevHeightRef.current;
            prevHeightRef.current = newHeight;

            if (delta > 0 && atBottomRef.current && scrollerRef.current) {
                scrollerRef.current.scrollTop += delta;
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [scrollerRef, atBottomRef]);

    return (
        <div ref={contentRef} className="flex flex-col items-center">
            {children}
        </div>
    );
};

interface Props {
    chatSessionKey: ChatSessionKey;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

const MessageList = ({ chatSessionKey, videoDark = false }: Props) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const { i18next } = useAppContext();
    const scrollerRef = useRef<HTMLDivElement>(null);
    // One Tab stop for the whole list, entered at the newest message;
    // the arrows move between messages.
    const rovingHandlers = useRovingMessageList(scrollerRef);
    const atBottomRef = useRef(true);
    const prevScrollHeightRef = useRef(0);
    const prevClientHeightRef = useRef(0);
    const prevFirstKeyRef = useRef<string | undefined>();
    const prevLastKeyRef = useRef<string | undefined>();

    const {
        items,
        ready,
        lastMessageIsOwn,
        markReadIfNeeded,
        loadOlderMessages,
        isLoadingOlder,
    } = useMessageListData(chatSessionKey);

    // Reset at-bottom when switching chats so the new chat always starts
    // at bottom, regardless of where the previous chat was scrolled.
    const prevChatKeyRef = useRef(chatSessionKey);
    if (prevChatKeyRef.current !== chatSessionKey) {
        prevChatKeyRef.current = chatSessionKey;
        atBottomRef.current = true;
    }

    // Get the first message/post key for prepend detection
    const firstMsgKey = items.find(
        (item) => item.type === 'message' || item.type === 'post',
    );
    const currentFirstKey =
        firstMsgKey &&
        (firstMsgKey.type === 'message' || firstMsgKey.type === 'post')
            ? firstMsgKey.chatMessageKey
            : undefined;

    // Newest message key, so a message of your own can be told apart from the
    // same message being re-rendered for some other reason.
    const lastMsgKey = [...items]
        .reverse()
        .find((item) => item.type === 'message' || item.type === 'post');
    const currentLastKey =
        lastMsgKey &&
        (lastMsgKey.type === 'message' || lastMsgKey.type === 'post')
            ? lastMsgKey.chatMessageKey
            : undefined;

    const isAtBottom = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return false;
        return (
            el.scrollHeight - el.scrollTop - el.clientHeight <
            AT_BOTTOM_THRESHOLD
        );
    }, []);

    const scrollToBottom = useCallback((instant = true) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTo({
            top: el.scrollHeight,
            behavior: instant ? 'instant' : 'smooth',
        });
    }, []);

    // Scroll to bottom on initial mount and when switching chats.
    useLayoutEffect(() => {
        if (ready) {
            scrollToBottom();
            atBottomRef.current = true;
            prevScrollHeightRef.current =
                scrollerRef.current?.scrollHeight ?? 0;
            markReadIfNeeded();
        }
        prevFirstKeyRef.current = currentFirstKey;
        prevLastKeyRef.current = currentLastKey;
    }, [chatSessionKey, ready]);

    // Handle new messages: scroll to bottom if already there
    // Handle prepend: preserve scroll position
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el || !ready) return;

        // Only when the newest message is one that just arrived - otherwise a
        // rebuild for an unrelated reason would drag a reader back down.
        const sentByYou =
            lastMessageIsOwn && prevLastKeyRef.current !== currentLastKey;

        // Detect prepend: first message key changed
        if (
            prevFirstKeyRef.current &&
            currentFirstKey &&
            currentFirstKey !== prevFirstKeyRef.current
        ) {
            const addedHeight = el.scrollHeight - prevScrollHeightRef.current;
            if (addedHeight > 0) {
                el.scrollTop += addedHeight;
            }
        } else if (atBottomRef.current || sentByYou) {
            // New message at the end - stay at bottom. A message of your own
            // brings you down to it however far back you were reading: you
            // just wrote it, so not showing it reads as the send having failed.
            scrollToBottom();
            atBottomRef.current = true;
            markReadIfNeeded();
        }

        prevScrollHeightRef.current = el.scrollHeight;
        prevFirstKeyRef.current = currentFirstKey;
        prevLastKeyRef.current = currentLastKey;
    }, [items]);

    // Re-pin when the viewport itself shrinks, which happens whenever something
    // takes height from the list: the composer growing to several lines, the
    // dialpad opening, the window resizing. Nothing here changes the content, so
    // ContentWrapper's observer never fires for it -- without this the newest
    // message slides behind the composer as you type.
    //
    // Deliberately does not consult atBottomRef: losing height pushes us further
    // from the bottom than AT_BOTTOM_THRESHOLD, so the resulting scroll event
    // clears that flag before this runs. Instead, measure against the height just
    // lost, which reconstructs whether we were at the bottom beforehand and does
    // not depend on the order the two events arrive in.
    //
    // Someone who had scrolled back to read is further away than the height lost,
    // so they stay where they are rather than being yanked to the end.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        prevClientHeightRef.current = el.clientHeight;

        const observer = new ResizeObserver(() => {
            const heightLost = prevClientHeightRef.current - el.clientHeight;
            prevClientHeightRef.current = el.clientHeight;
            if (heightLost <= 0) return;

            const distanceFromBottom =
                el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceFromBottom <= heightLost + AT_BOTTOM_THRESHOLD) {
                scrollToBottom();
                atBottomRef.current = true;
                prevScrollHeightRef.current = el.scrollHeight;
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
        // `ready` gates whether the scroller is mounted at all - while the
        // skeleton is showing there is no element to observe, so this has to
        // re-run once it appears.
    }, [scrollToBottom, ready]);

    const handleScroll = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;

        atBottomRef.current = isAtBottom();

        if (atBottomRef.current) {
            markReadIfNeeded();
        }

        if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
            loadOlderMessages();
        }
    }, [isAtBottom, markReadIfNeeded, loadOlderMessages]);

    const handleDrop = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            const file = event.dataTransfer.files[0];
            if (file) {
                if (file.size > MAX_ALLOWED_ATTACHMENT_SIZE) {
                    openModal('messageAttachmentTooLarge');
                    return;
                }
                dispatch(
                    attachmentCompose(chatSessionKey, {
                        name: file.name,
                        file,
                    }),
                );
            }
        },
        [chatSessionKey, dispatch, openModal],
    );

    const renderItem = (item: ListItem) => {
        switch (item.type) {
            case 'date-divider':
                return (
                    <DateDivider
                        key={`date-${item.date}`}
                        date={item.date}
                        videoDark={videoDark}
                    />
                );
            case 'unread-divider':
                return (
                    <UnreadDivider key="unread-divider" videoDark={videoDark} />
                );
            case 'message':
                return (
                    <ChatMessageRow
                        key={`msg-${item.chatMessageKey}`}
                        chatSessionKey={chatSessionKey}
                        chatMessageKey={item.chatMessageKey}
                        showAvatar={item.showAvatar}
                        videoDark={videoDark}
                    />
                );
            case 'post':
                return (
                    <ChatPostRow
                        key={`post-${item.chatMessageKey}`}
                        chatSessionKey={chatSessionKey}
                        chatMessageKey={item.chatMessageKey}
                        videoDark={videoDark}
                    />
                );
            case 'external':
                // Rendered from the item rather than selected by key: guest
                // messages are relay state and never enter state.chats.
                return (
                    <ExternalChatMessage
                        key={`ext-${item.message.get('key')}`}
                        message={item.message}
                        showAvatar={item.showAvatar}
                        videoDark={videoDark}
                    />
                );
        }
    };

    const skeleton = (
        <div className="relative flex w-full flex-col items-center">
            <GenericListSkeleton
                isLarge={true}
                count={5}
                videoDark={videoDark}
            />
        </div>
    );

    if (!ready) {
        return (
            <main
                data-test-id="message-list"
                className="scrollbar flex-1 items-end pt-6"
            >
                {skeleton}
            </main>
        );
    }

    return (
        <Scroller
            ref={scrollerRef}
            data-test-id="message-list"
            role="feed"
            aria-label={i18next.t('route.messages')}
            aria-busy={isLoadingOlder}
            {...rovingHandlers}
            onScroll={handleScroll}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="flex-1 items-end pt-6"
        >
            <MessageThreadCheckProvider value={false}>
                <ContentWrapper
                    scrollerRef={scrollerRef}
                    atBottomRef={atBottomRef}
                >
                    <div className="w-full max-w-2xl">
                        {groupItemsByDate(items).map((group) => (
                            /* One box per day, so the day's pinned header is
                               pushed out by the bottom of its own day rather
                               than staying stuck for the whole conversation.
                               A sticky element can only travel within its
                               nearest block ancestor, so without this the
                               headers pile up at the same offset and the
                               newest simply paints over the rest. */
                            <div key={group.key}>
                                {group.items.map(renderItem)}
                            </div>
                        ))}
                    </div>
                    <div className="h-3" />
                </ContentWrapper>
            </MessageThreadCheckProvider>
        </Scroller>
    );
};

export default MessageList;
